import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AttachmentsSelectDialogComponent, HarvestSelectDialogComponent } from 'app/dialogs';

import * as models from 'app/models';
import * as services from 'app/services';
import * as viewmodels from 'app/viewmodels';
import { DocumentData, QuerySnapshot } from 'firebase/firestore';
import { Subscription } from 'rxjs';

type SectionKey = 'cloud' | 'attachments' | 'notes' | 'subtasks' | 'data';

@Component({
    selector: 'app-task-library-editor',
    templateUrl: './task-library-editor.component.html',
    styleUrls: ['./task-library-editor.component.scss'],
    standalone: false
})
export class TaskLibraryEditorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() id: string;
  @Input() allTags: string[];
  @Output() saved: EventEmitter<models.TemplatedTask> = new EventEmitter();
  userRole: models.Role;

  templatedTask: models.TemplatedTask;
  initialTemplatedTask: models.TemplatedTask;

  saving: boolean = false;

  sections: Record<SectionKey, boolean> = {
    cloud: false,
    attachments: false,
    notes: false,
    subtasks: false,
    data: false,
  };
  expandedSubtasks = new Set<models.Subtask>();

  dataLibrary: models.DataCollectionDefinition[] = [];
  units: viewmodels.DataCollectionDefinitionUnit[] = new viewmodels.DataCollectionDefinitionUnits().list;
  private dataLibrarySub: Subscription;

  get dirty() {
    let jTask = JSON.stringify(this.templatedTask);
    let iTask = JSON.stringify(this.initialTemplatedTask);

    return jTask != iTask;
  }

  nameFocusState: boolean = false;

  get cloudCount(): number { return this.templatedTask?.cloudAttachments?.length ?? 0; }
  get attachmentCount(): number { return this.templatedTask?.attachments?.length ?? 0; }
  get noteCount(): number { return this.templatedTask?.notes?.length ?? 0; }
  get subtaskCount(): number { return this.templatedTask?.subtasks?.length ?? 0; }
  get dataCount(): number { return this.templatedTask?.dataCollectionDefinitions?.length ?? 0; }

  get subtasksPreview(): string {
    if (this.subtaskCount === 0) { return 'No subtasks'; }
    return this.templatedTask.subtasks.map(s => s.title || 'Untitled').join(' · ');
  }

  get dataPreview(): string {
    if (this.dataCount === 0) { return 'No data fields'; }
    return this.templatedTask.dataCollectionDefinitions.map(d => d?.name || 'Not selected').join(' · ');
  }

  get notesPreview(): string {
    if (this.noteCount === 0) { return 'No notes'; }
    return this.templatedTask.notes.join(' · ');
  }

  get attachmentsPreview(): string {
    if (this.attachmentCount === 0) { return 'No files'; }
    return `${this.attachmentCount} file${this.attachmentCount === 1 ? '' : 's'} attached`;
  }

  get cloudPreview(): string {
    if (this.cloudCount === 0) { return 'No links'; }
    return this.templatedTask.cloudAttachments.map(l => this.hostOf(l)).join(' · ');
  }

  constructor(
    private templatedTaskService: services.TemplatedTasksService
    , private templateService: services.TemplateService
    , private harvestService: services.HarvestService
    , private helperService: services.HelperService
    , private claimsService: services.ClaimsService
    , private dataLibraryService: services.DataLibraryService
    , private firestore: AngularFirestore
    , private dialog: MatDialog
    , private router: Router
    , private notifierService: services.NotifierService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userRole = await this.claimsService.getRoleAwait();
    this.dataLibrarySub = this.dataLibraryService.getAllSubscribe().subscribe(data => {
      if (data) {
        this.dataLibrary = this.helperService.sortArrayByStringField(data.list, 'name');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.dataLibrarySub) { this.dataLibrarySub.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.id != null && changes.id.currentValue != changes.id.previousValue) {
      let id = changes.id.currentValue;
      this.collapseSections();
      if(id == 'add') {
        this.createAddedTask();
      } else {
        this.getTemplatedTask(changes.id.currentValue);
      }
    }
  }

  createAddedTask() {
    this.templatedTask = {
      anyTime: true,
      startTime: '12:00',
    }
  }

  getTemplatedTask(id: string) {
    if(id == null) {
      this.templatedTask = null;
      this.initialTemplatedTask = null;
      return;
    }
    this.templatedTaskService.get(id).then(templatedTask => {
      this.templatedTask = templatedTask;
      this.initialTemplatedTask = this.helperService.deepCopy(templatedTask);
    });
  }

  toggleSection(key: SectionKey) {
    this.sections[key] = !this.sections[key];
  }

  collapseSections() {
    (Object.keys(this.sections) as SectionKey[]).forEach(k => this.sections[k] = false);
    this.expandedSubtasks.clear();
  }

  hostOf(link: string): string {
    try {
      return new URL(link).hostname.replace(/^www\./, '');
    } catch {
      return link || 'Empty link';
    }
  }

  async save() {
    if(this.templatedTask.name == null || this.templatedTask.name == ''){
      this.notifierService.error('Please enter a name for the Templated Task');
      return;
    }

    let emptySubtasks = (this.templatedTask.subtasks ?? []).filter(s => s.title == null || s.title == '');
    if(emptySubtasks.length > 0) {
      this.notifierService.error('Please fill out all subtasks');
      this.sections.subtasks = true;
      return;
    }

    // drop rows the user added but never filled in
    if(this.templatedTask.notes != null) {
      this.templatedTask.notes = this.templatedTask.notes.map(n => n?.trim()).filter(n => n != null && n != '');
    }
    if(this.templatedTask.cloudAttachments != null) {
      this.templatedTask.cloudAttachments = this.templatedTask.cloudAttachments.map(l => l?.trim()).filter(l => l != null && l != '');
    }
    if(this.templatedTask.dataCollectionDefinitions != null) {
      this.templatedTask.dataCollectionDefinitions = this.templatedTask.dataCollectionDefinitions.filter(d => d?.id != null);
    }

    this.saving = true;
    let id = await this.templatedTaskService.save(this.templatedTask);
    if(this.id == 'add') {
      this.templatedTask.id = id;
      this.helperService.addQueryStringParam(models.queryStringKeys.Templatedtask, id);
    } else {
      this.id = id;
    }
    this.initialTemplatedTask = this.helperService.deepCopy(this.templatedTask);

    this.notifierService.success('Successfully saved Templated Task');

    //check templates the task is used in and update them
    await this.updateTemplates();

    this.saving = false;
    this.saved.emit(this.templatedTask);
  }

  undo() {
    if(this.id == 'add') {
      this.router.navigate(['/console', 'templates', 'task-library']);
    } else {
      this.templatedTask = this.helperService.deepCopy(this.initialTemplatedTask);
      this.expandedSubtasks.clear();
    }
  }

  delete() {
    this.notifierService.confirm(
      'Delete task?',
      `This permanently removes "${this.templatedTask.name}" from the Task Library. Templates already using it keep their copy.`,
      () => {
        this.templatedTaskService.delete(this.id).then(() => {
          this.router.navigate(['/console', 'templates', 'task-library']);
          this.saved.emit(null);
        });
      },
      () => {},
      'Delete'
    );
  }

  // --- Cloud links -----------------------------------------------------------
  addCloudLink() {
    if(this.templatedTask.cloudAttachments == null) { this.templatedTask.cloudAttachments = []; }
    this.templatedTask.cloudAttachments.push('');
  }

  removeCloudLink(index: number) {
    this.templatedTask.cloudAttachments.splice(index, 1);
  }

  // --- Attachments -------------------------------------------------------------
  attachmentsClick() {
    let notesDialog = this.dialog.open(AttachmentsSelectDialogComponent, { data: { selected: this.templatedTask.attachments } });

    notesDialog.afterClosed().subscribe(data => {
      if (data) {
        this.templatedTask.attachments = data;
      }
    })
  }

  removeAttachment(index: number) {
    this.templatedTask.attachments.splice(index, 1);
  }

  // --- Notes ---------------------------------------------------------------------
  addNote() {
    if(this.templatedTask.notes == null) { this.templatedTask.notes = []; }
    this.templatedTask.notes.push('');
  }

  removeNote(index: number) {
    this.templatedTask.notes.splice(index, 1);
  }

  // --- Subtasks --------------------------------------------------------------------
  addSubtask() {
    if(this.templatedTask.subtasks == null) { this.templatedTask.subtasks = []; }
    this.templatedTask.subtasks.push({
      id: this.firestore.createId(),
      title: '',
      description: '',
      completed: false
    });
  }

  removeSubtask(index: number) {
    let removed = this.templatedTask.subtasks.splice(index, 1);
    removed.forEach(s => this.expandedSubtasks.delete(s));
  }

  dropSubtask(event: CdkDragDrop<models.Subtask[]>) {
    moveItemInArray(this.templatedTask.subtasks, event.previousIndex, event.currentIndex);
  }

  toggleSubtaskDescription(subtask: models.Subtask) {
    if(this.expandedSubtasks.has(subtask)) {
      this.expandedSubtasks.delete(subtask);
    } else {
      this.expandedSubtasks.add(subtask);
    }
  }

  hasDescription(subtask: models.Subtask): boolean {
    return subtask.description != null && subtask.description != '';
  }

  // --- Data collection -----------------------------------------------------------------
  addDataDef() {
    if(this.templatedTask.dataCollectionDefinitions == null) { this.templatedTask.dataCollectionDefinitions = []; }
    this.templatedTask.dataCollectionDefinitions.push({ id: null, name: null, unit: null });
  }

  removeDataDef(index: number) {
    this.templatedTask.dataCollectionDefinitions.splice(index, 1);
  }

  dropDataDef(event: CdkDragDrop<models.DataCollectionDefinition[]>) {
    moveItemInArray(this.templatedTask.dataCollectionDefinitions, event.previousIndex, event.currentIndex);
  }

  onDataDefChange(index: number, definitionId: string) {
    let definition = this.dataLibrary.find(d => d.id == definitionId);
    if(definition != null) {
      this.templatedTask.dataCollectionDefinitions[index] = this.helperService.deepCopy(definition);
    }
  }

  getUnit(unitId: string): string {
    let unit = this.units.find(u => u.id == unitId);
    if(unit == null) { return ''; }
    let retString = unit.display;
    if(unit.suffix) retString += ` (${unit.suffix})`;
    return retString;
  }

  openDataLibrary() {
    window.open('/console/grow-settings/data-library', '_blank');
  }

  // --- Template / harvest sync (unchanged) -----------------------------------------------
  async updateTemplates() {
    let request: models.UpdateStepsFromTemplatedTaskRequest = {
      companyId: this.helperService.currentCompanyId,
      templatedTaskId: this.templatedTask.id
    }
    let response = await this.templateService.updateStepsFromTemplatedTask(request);

    if(response.data.success){
      if(response.data.value.length > 0){
        this.notifierService.success(`Successfully updated ${response.data.value.length} Templates - ${response.data.value.map(t => t.name).join(', ')}`);

        await this.checkConnectedHarvests(response.data.value);
      }
    }
  }

  async checkConnectedHarvests(templates: models.Template[]) {
    let requests = [];
    let templateIds = templates.map(t => t.uid)
    templateIds.forEach(templateId => {
      requests.push(this.harvestService.getAllForTemplate(templateId).get().toPromise());
    })
    let responses: QuerySnapshot<DocumentData>[] = await Promise.all(requests);

    let harvests: Array<models.Harvest> = [];

    responses.forEach(response => {
      if(response.size == 0){
        return;
      }
      response.forEach(doc => {
        let harvest: models.Harvest = doc.data()
        harvest.uid = doc.id;
        harvests.push(harvest);
      })
    });

    harvests = this.helperService.sortArrayByStringField(harvests, 'name');

    let inModel = {
      header: 'Templates Saved.  Would you like to update Harvests?',
      negativeBtn: 'No',
      affirmativeBtn: 'Update',
      //templateId: templateId,
      harvests: harvests
    }

    let dialogRef = this.dialog.open(HarvestSelectDialogComponent, {
      data: inModel
    });

    dialogRef.afterClosed().subscribe(async (dialogResult: models.Harvest[]) => {
      if(dialogResult){

        templates.forEach(async template => {
          let templateHarvestsSelected = dialogResult.filter(h => h.templateId == template.uid);
          if(templateHarvestsSelected.length == 0){
            return;
          }
          this.kickOffHarvestUpdate(template, templateHarvestsSelected);
        });
      }
    })
  }

  async kickOffHarvestUpdate(template: models.Template, harvests: models.Harvest[]) {
    this.notifierService.success(`Harvest Update kicked off for Template - ${template.name}.  Go to processes screen to view.`);
    let response = await this.harvestService.updateHarvestsFromTemplate(template.uid, harvests.map(h => h.uid));
    let result: models.Result<number> = response.data;

    if(result.success){
      this.notifierService.success(`Successfully updated ${result.value} Harvests for Template - ${template.name}`);
    }
  }
}
